/**
 * Uniform Manager - Manages custom user-defined uniforms
 * Handles type validation, serialization, and Float32Array generation
 */

// Supported uniform types with their sizes in floats
const UNIFORM_TYPES = {
  float: { size: 1, default: 0 },
  vec2: { size: 2, default: [0, 0] },
  vec3: { size: 3, default: [0, 0, 0] },
  vec4: { size: 4, default: [0, 0, 0, 0] },
  color: { size: 3, default: [1, 1, 1] }, // RGB normalized 0-1
  bool: { size: 1, default: 0 }, // 0 or 1
};

// Maximum custom uniforms buffer size (224 bytes = 56 floats)
const MAX_CUSTOM_FLOATS = 56;

export class UniformManager {
  constructor() {
    /** @type {Map<string, UniformDefinition>} */
    this.uniforms = new Map();
    this.onChangeCallback = null;
  }

  /**
   * Add a new uniform
   * @param {Object} definition - Uniform definition
   * @param {string} definition.name - Unique name (e.g., "uSpeed")
   * @param {string} definition.type - Type: 'float', 'vec2', 'vec3', 'vec4', 'color', 'bool'
   * @param {number|number[]} [definition.value] - Initial value
   * @param {number} [definition.min] - Min value for slider
   * @param {number} [definition.max] - Max value for slider
   * @param {number} [definition.step] - Step for slider
   * @returns {{ success: boolean, error?: string }}
   */
  add(definition) {
    const { name, type } = definition;

    // Validate name
    if (!name || typeof name !== 'string') {
      return { success: false, error: 'Name is required' };
    }
    if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(name)) {
      return { success: false, error: 'Invalid name: must start with letter or underscore' };
    }
    if (this.uniforms.has(name)) {
      return { success: false, error: `Uniform "${name}" already exists` };
    }

    // Validate type
    if (!UNIFORM_TYPES[type]) {
      return { success: false, error: `Invalid type: ${type}` };
    }

    // Check size limit
    const currentSize = this.getTotalFloatSize();
    const typeInfo = UNIFORM_TYPES[type];
    if (currentSize + typeInfo.size > MAX_CUSTOM_FLOATS) {
      return { success: false, error: 'Maximum uniform buffer size exceeded' };
    }

    // Create uniform entry
    const defaultValue = definition.value ?? structuredClone(typeInfo.default);
    const uniform = {
      name,
      type,
      value: this.normalizeValue(type, defaultValue),
      defaultValue: this.normalizeValue(type, defaultValue),
      min: definition.min ?? 0,
      max: definition.max ?? (type === 'bool' ? 1 : 1),
      step: definition.step ?? (type === 'bool' ? 1 : 0.01),
    };

    this.uniforms.set(name, uniform);
    this.notifyChange();
    return { success: true };
  }

  /**
   * Remove a uniform
   * @param {string} name
   * @returns {boolean}
   */
  remove(name) {
    if (this.uniforms.delete(name)) {
      this.notifyChange();
      return true;
    }
    return false;
  }

  /**
   * Update uniform value
   * @param {string} name
   * @param {number|number[]} value
   */
  setValue(name, value) {
    const uniform = this.uniforms.get(name);
    if (uniform) {
      uniform.value = this.normalizeValue(uniform.type, value);
      // Note: Don't call notifyChange here - value changes shouldn't trigger full refresh
      // The UI handles value updates directly through input events
    }
  }

  /**
   * Get uniform value
   * @param {string} name
   * @returns {number|number[]|null}
   */
  getValue(name) {
    return this.uniforms.get(name)?.value ?? null;
  }

  /**
   * Get all uniforms as array
   * @returns {Array<Object>}
   */
  getAll() {
    return Array.from(this.uniforms.values());
  }

  /**
   * Get uniform count
   * @returns {number}
   */
  getCount() {
    return this.uniforms.size;
  }

  /**
   * Check if uniforms exist
   * @returns {boolean}
   */
  hasUniforms() {
    return this.uniforms.size > 0;
  }

  /**
   * Reset all uniforms to default values
   */
  resetToDefaults() {
    for (const uniform of this.uniforms.values()) {
      uniform.value = structuredClone(uniform.defaultValue);
    }
    this.notifyChange();
  }

  /**
   * Clear all uniforms
   */
  clear() {
    this.uniforms.clear();
    this.notifyChange();
  }

  /**
   * Get total size in floats
   * @returns {number}
   */
  getTotalFloatSize() {
    let size = 0;
    for (const uniform of this.uniforms.values()) {
      size += UNIFORM_TYPES[uniform.type].size;
    }
    return size;
  }

  /**
   * Generate Float32Array for uniform buffer (custom uniforms only)
   * @returns {Float32Array}
   */
  toFloat32Array() {
    const data = [];
    for (const uniform of this.uniforms.values()) {
      const value = uniform.value;
      if (Array.isArray(value)) {
        data.push(...value);
      } else {
        data.push(value);
      }
    }
    return new Float32Array(data);
  }

  /**
   * Normalize value to correct type format
   * @param {string} type
   * @param {number|number[]} value
   * @returns {number|number[]}
   */
  normalizeValue(type, value) {
    const typeInfo = UNIFORM_TYPES[type];

    if (type === 'float' || type === 'bool') {
      const num = Array.isArray(value) ? value[0] : value;
      return type === 'bool' ? (num ? 1 : 0) : Number(num) || 0;
    }

    // Vector types
    if (Array.isArray(value)) {
      const arr = value.slice(0, typeInfo.size).map((v) => Number(v) || 0);
      while (arr.length < typeInfo.size) arr.push(0);
      return arr;
    }

    // Single number for vector - replicate
    const num = Number(value) || 0;
    return new Array(typeInfo.size).fill(num);
  }

  /**
   * Generate Slang struct declaration for custom uniforms
   * @returns {string}
   */
  generateDeclaration() {
    if (this.uniforms.size === 0) return '';

    const lines = [];
    for (const uniform of this.uniforms.values()) {
      const slangType = this.getSlangType(uniform.type);
      lines.push(`  ${slangType} ${uniform.name};`);
    }
    return lines.join('\n');
  }

  /**
   * Get Slang type name
   * @param {string} type
   * @returns {string}
   */
  getSlangType(type) {
    switch (type) {
      case 'float':
      case 'bool':
        return 'float';
      case 'vec2':
        return 'float2';
      case 'vec3':
      case 'color':
        return 'float3';
      case 'vec4':
        return 'float4';
      default:
        return 'float';
    }
  }

  /**
   * Serialize state for persistence
   * @returns {Array}
   */
  serialize() {
    const uniforms = [];
    for (const uniform of this.uniforms.values()) {
      uniforms.push({
        n: uniform.name,
        t: uniform.type,
        v: uniform.value,
        d: uniform.defaultValue,
        mi: uniform.min,
        ma: uniform.max,
        s: uniform.step,
      });
    }
    return uniforms;
  }

  /**
   * Deserialize state
   * @param {Array} data
   */
  deserialize(data) {
    this.uniforms.clear();
    if (!Array.isArray(data)) return;

    for (const item of data) {
      if (!item.n || !item.t || !UNIFORM_TYPES[item.t]) continue;

      const typeInfo = UNIFORM_TYPES[item.t];
      this.uniforms.set(item.n, {
        name: item.n,
        type: item.t,
        value: this.normalizeValue(item.t, item.v ?? typeInfo.default),
        defaultValue: this.normalizeValue(item.t, item.d ?? typeInfo.default),
        min: item.mi ?? 0,
        max: item.ma ?? 1,
        step: item.s ?? 0.01,
      });
    }
    this.notifyChange();
  }

  /**
   * Set change callback
   * @param {Function} callback
   */
  setOnChange(callback) {
    this.onChangeCallback = callback;
  }

  /**
   * Notify that uniforms changed
   */
  notifyChange() {
    if (this.onChangeCallback) {
      this.onChangeCallback();
    }
  }
}

// Export type info for UI
export { UNIFORM_TYPES, MAX_CUSTOM_FLOATS };
